import {Card} from '../model/card';
import { Good } from '../model/good';
import { Order } from '../model/order';
import { User, UserSession } from '../model/user';
import {ScrmGwUser, ScrmUser} from '../model/scrm_user';
import { CardResponse } from './card';
import { ResponseData, TimeResponse} from './common';
import { LoginResponse } from './login';
import { OrderResponse } from './order';
import {ProductResponse, ProductResponseByAdmin} from './product';
import { UserResponse } from './user';
import moment, {Dayjs} from "dayjs";
import { defaultCurrency } from '../model/currency';
import {ScrmGwUserResponse, ScrmQwUserResponse} from "@/repo/response/scrm_user";
import {SecondSaleYearCard} from "@/repo/model/second-sale-card";
import {SecondSaleYearCardResponse} from "@/repo/response/second-sale-card";

export function responseTransfer<T, R>(response: ResponseData<T>, convert: (data: T) => R, onError?: (r: ResponseData<any>) => void): R {
    if (response.code !== 1) if (onError) onError(response); else throw response.message;
    return convert(response.data);
}

export function userResponseTransfer(v: UserResponse): User {
    return {
        id: v.id.toString(),
        login: v.name,
        name: v.weixinName,
        phone: v.phone,
        avatar: v.imgUrl,
        cardCount: v.yearCardCount,
        permissions: v.userRoles?.map(v => v.toString()),
        adviserGroup: v.adviserGroup,
        adviserGroupName: v.adviserGroupName,
        permissionCount: v.userRoles?.length,
        status: v.isAgreeProtocol ? 1 : 0,
        encrypt: {
            name: v.encryptName,
            phone: v.encryptPhone
        }
    }
}



export function scrmQwUserResponseTransfer(v: ScrmQwUserResponse): ScrmUser {
    return {
        customer_unionid_biz: v.customer_unionid_biz.toString(),
        customer_nickname: v.customer_nickname,
        customer_avatar: v.customer_avatar,
        customer_phone: v.customer_phone,
        customer_add_time: v.customer_add_time,
        guwen_remark: v.guwen_remark,
        customer_origin: v.customer_origin,
        customer_add_qudao: v.customer_add_qudao,
        guwen_phone: v.guwen_phone,
        guwen_nickname: v.guwen_nickname,
        card_count: v.card_count
    }
}


export function scrmGwUserResponseTransfer(v: ScrmGwUserResponse): ScrmGwUser {
    return {
        customer_unionid_biz: v.customer_unionid_biz.toString(),
        customer_nickname: v.customer_nickname,
        customer_avatar: v.customer_avatar,
        customer_phone: v.customer_phone,
        customer_wx_account: v.customer_wx_account,
        customer_add_time: v.customer_add_time,
        guwen_remark: v.guwen_remark,
        customer_origin: v.customer_origin,
        customer_add_qudao: v.customer_add_qudao,
        guwen_phone: v.guwen_phone,
        guwen_nickname: v.guwen_nickname,
        guwen_account_wx: v.guwen_account_wx,
        card_count: v.card_count
    }
}



export function loginResponseTransfer(v: LoginResponse): UserSession {
    const user = userResponseTransfer(v);
    return {
        user: user,
        payId: '',
        token: v.token,
        expireTime: '',
        permissions: user.permissions ?? [],
    }
}

export function cardResponseTransfer(v: CardResponse): Card {
    let present: string|undefined;
    let count = v.productList?.length;
    const items = v.productList?.map(p => {
        const type = p.productType;
        if (type === 2) {
            present = p.content;
            count--;
        }
        return {
            good: goodResponseTransfer(p),
            count: p.count,
            consume: p.orderUsedCount
        }
    });
    return {
        id: v.id.toString(),
        createTime: timeResponseTransfer(v.boughtAt),
        items: items,
        name: v.name ?? v.yearCardName,
        cover: v.imgUrl,
        birthdayPresent: present,
        deliveryCount: v.lastTimes,
        externalNumber: v.orderNo,
        externalPlatform: v.platform,
        card: v.yearCardId ? { id: v.yearCardId.toString() } : undefined,
        user: v.userInfoView ? userResponseTransfer(v.userInfoView) : v.userId ? { id: v.userId.toString() } : undefined,
        itemCount: count,
        agentUser: v.createUserName ? { id: '',
            name: v.createUserName,
            phone: v.createUserPhone
        } : undefined,
        company: v.companyTagView ? {
            id: v.companyTagView.id.toString(),
            name: v.companyTagView.name
        } : undefined,
        value: v.money ? (v.money / 100).toString() : undefined,
        currency: defaultCurrency,
        status: 1,
        isStokeWarning: v.isStokeWarning
    }
}

export function goodResponseTransfer(v: ProductResponse): Good {
    return {
        id: v.id.toString(),
        name: v.name,
        icon: v.imgUrl,
        description: v.content,
        cover: v.horizontalImgUrl,
        productCategoryTagId: v.productCategoryTagId,
        productCategoryName: v.productCategoryName,
        status: 0
            // v.productType === 2 ? -1 : 0
    }
}

export function goodResponseTransferByAdminProduct(v: ProductResponseByAdmin): Good {
    return {
        id: v.id.toString(),
        name: v.name,
        icon: v.imgUrl,
        description: v.content,
        cover: v.horizontalImgUrl,
        type: v.productType?.toString(),
        number: v.productNumber,
        productCategoryTagId: v.productCategoryTagId,
        productCategoryName: v.productCategoryName,
        status: v.status
    }
}

export function orderResponseTransfer(v: OrderResponse): Order {
    const card =  v.yearCardInfo ? cardResponseTransfer(v.yearCardInfo) : undefined;
    const status = v.status;
    return {
        name: v.orderNo,
        number: v.id.toString(),
        createTime: timeResponseTransfer(v.createAt),
        modifyTime: timeResponseTransfer(v.updateAt),
        finishTime: timeResponseTransfer(v.finishAt),
        processStatus: v.afterSaleStatus,
        summary: v.sellerRemark ?? v.remark,
        card: card,
        status: status === 4 ? 127 : status === 5 ? -127 : status,
        user: v.buyerUser ? userResponseTransfer(v.buyerUser) : v.buyerUserId ? {
            id: v.buyerUserId.toString(),
            phone: v.buyerPhone,
        } : undefined,
        itemCount: v.products?.length,
        items: v.products?.map(p => ({
            count: p.count,
            card: card,
            good: goodResponseTransfer(p),
        })),
        deliveries: [{
            number: v.externalBillNum,
            createTime: timeResponseTransfer(v.deliveryAt),
            modifyTime: timeResponseTransfer(v.deliveryAt),
            name: v.receiverName,
            phone: v.receiverPhone,
            address: v.receiverDetailAddress,
            addressLine1: v.receiverCityName,
            addressLine2: v.receiverProvinceName,
            externalNumber: v.deliveryOrderNo,
            externalPlatform: v.deliveryOrderAgent,
            status: 0,
            encrypt: v.encryptReceiverName || v.encryptReceiverPhone || v.encryptAddress ? {
                name: v.encryptReceiverName ?? v.receiverName,
                phone: v.encryptReceiverPhone ?? v.receiverPhone,
                address: v.encryptAddress ?? v.receiverDetailAddress,
            } : undefined
        }, ],
    };
}

export function secondSaleYearCardResponseTransfer(v: SecondSaleYearCardResponse): SecondSaleYearCard {
    return {
        id: v.id.toString(),
        yearCardName: v.yearCardName,
        yearCardCover: v.yearCardImgUrl,
        customerUserId: v.customerUserId?.toString(),
        customerName: v.customerName,
        customerPhone: v.customerPhone,
        normalYearCardCount: v.normalYearCardCount,
        zeroStockYearCardCount: v.zeroStockYearCardCount,
        deletedYearCardCount: v.deletedYearCardCount,
        firstSaleAdviserUserId: v.firstSaleAdviserUserId?.toString(),
        firstSaleAdviserName: v.firstSaleAdviserName,
        firstSaleAdviserPhone: v.firstSaleAdviserPhone,
        adviserUserId: v.adviserUserId?.toString(),
        adviserName: v.adviserName,
        adviserPhone: v.adviserPhone,
        cardCreateTime: timeResponseTransfer(v.cardCreateAt),
        yearCardNo: v.yearCardNo,
        money: v.money,
        monthStartStockCount: v.monthStartStockCount,
        consumedBoxCount: v.consumedBoxCount,
        remainingBoxCount: v.remainingBoxCount,
        hasDeliveryPlan: v.hasDeliveryPlan,
        lastDeliveryTime: timeResponseTransfer(v.lastDeliveryAt),
        expandedProduct: v.expandedProduct,
        expandedProductContent: v.expandedProductContent,
        otherMaintenance: v.otherMaintenance,
        maintenanceActionContent: v.maintenanceActionContent,
        birthdayGiftContent: v.birthdayGiftContent,
        birthday: formatDateOnly(v.birthday),
        bSampleReceived: v.bSampleReceived ?? v.bsampleReceived,
        quoted: v.quoted,
        remark: v.remark,
        remarkTime: timeResponseTransfer(v.remarkAt),
        categoryStats: v.categoryStats
    }
}

export function timeResponseTransfer(v?: TimeResponse|number|string): string|undefined {
    if (!v) return undefined;
    if (typeof v === 'number') {
        const d = new Date();
        d.setTime(v > 100000000000 ? v : v * 1000);
        return d.toISOString();
    } else if (typeof v === 'string') return v;
    const m = moment();
    m.set('year', v.year);
    m.set('month', v.month);
    m.set('day', v.day);
    m.set('hour', v.hours);
    m.set('minute', v.minutes);
    m.set('second', v.seconds);
    return m.toISOString();
}

function formatDateOnly(v?: TimeResponse|number|string): string|undefined {
    const time = timeResponseTransfer(v);
    return time ? moment(time).format('YYYY-MM-DD') : undefined;
}

export function toTimeResponse(v?: Dayjs): TimeResponse {
    v = v ?? moment();
    return {
        nanos: 0,
        timezoneOffset: 0,
        time: 0,
        date: v.date(),
        day: v.day(),
        month: v.month(),
        year: v.year(),
        hours: v.hour(),
        minutes: v.minute(),
        seconds: v.second()
    }
}
